/**
 * Query keys de hr — personas: nóminas, contratos, ausencias y empleados.
 *
 * Se componen en `src/lib/qk.ts`, que sigue siendo el único punto de import de
 * toda la app (`@/lib/qk`). Aquí solo vive la definición; añadir una clave nueva
 * de este dominio se hace en este fichero y aparece sola en `qk`.
 *
 * Las convenciones (entidad en singular primero, `companyId` después, subsegmentos
 * en medio) están explicadas en `src/lib/qk.ts` y siguen aplicando.
 */
export const hrKeys = {
  hr: {
    absences: (companyId?: string) => ['absences', companyId] as const,
    // Las de un empleado (ficha). Bajo el prefijo de `absences`: invalidar
    // `qk.hr.absences(cid)` también las refresca.
    absencesOf: (companyId?: string, employeeId?: string) =>
      ['absences', companyId, 'employee', employeeId] as const,
    absenceTypes: (companyId?: string) => ['absence-types', companyId] as const,
    collectiveAgreements: (companyId?: string) => ['collective-agreements', companyId] as const,
    contracts: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['contracts', companyId, filters] as const,
    contractByEmployee: (employeeId?: string) =>
      ['contracts-by-employee', employeeId] as const,
    timeEntries: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['time-entries', companyId, filters] as const,
    payrollRun: (runId?: string) =>
      (runId ? ['payroll-run', runId] : ['payroll-run']) as readonly unknown[],
    recurringItems: (companyId?: string, employeeId?: string) =>
      ['payroll-recurring-items', companyId, employeeId] as const,
    // Historial de auditoría de UN contrato, filtrado en servidor por `contract_id`.
    contractAuditLogs: (companyId?: string, contractId?: string) =>
      ['contract-audit-logs', companyId, contractId] as const,
  },

  // Fichaje del portal del empleado (TimesheetWidget, MiZonaPortalView). Bajo un
  // prefijo común: fichar invalida todo con `all()`.
  portalClock: {
    all: () => ['portal-clock'] as const,
    today: (companyId?: string, employeeId?: string, day?: string) =>
      ['portal-clock', companyId, employeeId, 'today', day] as const,
    week: (companyId?: string, employeeId?: string, weekStart?: string) =>
      ['portal-clock', companyId, employeeId, 'week', weekStart] as const,
    month: (companyId?: string, employeeId?: string, monthStart?: string) =>
      ['portal-clock', companyId, employeeId, 'month', monthStart] as const,
    history: (companyId?: string, employeeId?: string) =>
      ['portal-clock', companyId, employeeId, 'history'] as const,
    projects: (companyId?: string, employeeId?: string) =>
      ['portal-clock', companyId, employeeId, 'projects'] as const,
  },

  employee: {
    all: () => ['employee'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['employee', companyId, filters] as const,
    paginated: (
      companyId?: string,
      page = 0,
      pageSize = 10,
      filters: Record<string, unknown> = {},
    ) => ['employee', 'paginated', companyId, page, pageSize, filters] as const,
    byId: (id?: string) => ['employee', id] as const,
    org: (companyId?: string) => ['employee', 'org', companyId] as const,
    documents: (employeeId?: string) => ['employee', 'documents', employeeId] as const,
    expenses: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['employee', 'expenses', companyId, filters] as const,
    /** All skills for a company (allEmployeeSkills). */
    skills: (companyId?: string) => ['employee', 'all-skills', companyId] as const,
    /** Skills for a single employee (employeeSkills). */
    skillsOf: (employeeId?: string) => ['employee', 'skills-of', employeeId] as const,
  },
} as const;
